/**
 * TourOverlay — spotlight renderer for the tour engine.
 *
 * Joyride-class polish, zero dependencies:
 *  - animated spotlight that glides between targets, with a soft pulsing halo
 *  - popover card with entrance animation + arrow pointing at the target
 *  - smooth scrolling to off-screen targets, live tracking on scroll/resize
 *  - keyboard: → / Enter = next, ← = back, Esc = exit
 *
 * Design rules (ONBOARDING-SYSTEM-PLAN.md):
 *  - Never trap: Next/Skip always work; the spotlighted element stays
 *    CLICKABLE (backdrop is pointer-events: none) so "do the thing, then
 *    press Next" actually works.
 *  - Target missing → centered card fallback after a short wait.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Paper, Typography, Button, IconButton, Chip, GlobalStyles } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Above app chrome (appBar/drawer ≈ 1200) but BELOW MUI modals/menus (1300):
// when a tour step asks the user to open a dropdown (e.g. "pick Houston
// Plant"), the open menu must render on top of the tour card — at 2000 the
// card covered the menu options and the user couldn't click them.
const Z = 1250;
const CARD_W = 360;
const GAP = 18; // gap between target and card (leaves room for the arrow)
const PAD = 8; // spotlight padding around target
const ARROW = 12; // arrow square size

const tourKeyframes = (
  <GlobalStyles
    styles={{
      '@keyframes repsitTourPulse': {
        '0%': { boxShadow: '0 0 0 9999px rgba(8, 15, 28, 0.42), 0 0 0 0 rgba(99, 155, 255, 0.55)' },
        '70%': { boxShadow: '0 0 0 9999px rgba(8, 15, 28, 0.42), 0 0 0 14px rgba(99, 155, 255, 0)' },
        '100%': { boxShadow: '0 0 0 9999px rgba(8, 15, 28, 0.42), 0 0 0 0 rgba(99, 155, 255, 0)' },
      },
      '@keyframes repsitTourCardIn': {
        from: { opacity: 0, transform: 'translateY(10px) scale(0.98)' },
        to: { opacity: 1, transform: 'translateY(0) scale(1)' },
      },
      '@keyframes repsitTourFadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
    }}
  />
);

function getRect(target) {
  if (!target) return null;
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height, el };
}

/** Pick a side + position for the card, clamped to the viewport. */
function cardPosition(rect, placement, cardH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - 24);

  const below = rect.top + rect.height + GAP + PAD;
  const above = rect.top - GAP - PAD - cardH;
  const rightX = rect.left + rect.width + GAP + PAD;
  const leftX = rect.left - GAP - PAD - w;

  const fits = {
    bottom: below + cardH < vh - 12,
    top: above > 12,
    right: rightX + w < vw - 12,
    left: leftX > 12,
  };
  const order = {
    bottom: ['bottom', 'top', 'right', 'left'],
    top: ['top', 'bottom', 'right', 'left'],
    right: ['right', 'left', 'bottom', 'top'],
    left: ['left', 'right', 'bottom', 'top'],
  }[placement || 'bottom'];
  const side = order.find((s) => fits[s]) || 'bottom';

  let top;
  let left;
  if (side === 'bottom') {
    top = below;
    left = rect.left + rect.width / 2 - w / 2;
  } else if (side === 'top') {
    top = above;
    left = rect.left + rect.width / 2 - w / 2;
  } else if (side === 'right') {
    top = rect.top + rect.height / 2 - cardH / 2;
    left = rightX;
  } else {
    top = rect.top + rect.height / 2 - cardH / 2;
    left = leftX;
  }
  left = Math.max(12, Math.min(left, vw - w - 12));
  top = Math.max(12, Math.min(top, vh - cardH - 12));
  return { top, left, width: w, side };
}

/** Arrow position on the card edge, aimed at the target's center. */
function arrowStyle(rect, pos) {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const base = {
    position: 'absolute',
    width: ARROW,
    height: ARROW,
    bgcolor: 'background.paper',
    transform: 'rotate(45deg)',
    zIndex: -1,
  };
  if (pos.side === 'bottom') {
    return { ...base, top: -ARROW / 2, left: Math.max(14, Math.min(cx - pos.left - ARROW / 2, pos.width - 26)) };
  }
  if (pos.side === 'top') {
    return { ...base, bottom: -ARROW / 2, left: Math.max(14, Math.min(cx - pos.left - ARROW / 2, pos.width - 26)) };
  }
  if (pos.side === 'right') {
    return { ...base, left: -ARROW / 2, top: Math.max(14, Math.min(cy - pos.top - ARROW / 2, 200)) };
  }
  return { ...base, right: -ARROW / 2, top: Math.max(14, Math.min(cy - pos.top - ARROW / 2, 200)) };
}

export default function TourOverlay({ steps, stepIndex, onNext, onBack, onSkip }) {
  const step = steps[stepIndex];
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const [searching, setSearching] = useState(true);
  const cardRef = useRef(null);
  const [cardH, setCardH] = useState(190);

  // Navigate to the step's route if we're elsewhere.
  useEffect(() => {
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  // Find + track the target element (poll: tolerant of async page loads,
  // follows the element through scrolls, resizes, and layout shifts).
  useEffect(() => {
    let cancelled = false;
    let scrolled = false;
    const started = Date.now();
    setRect(null);
    setSearching(Boolean(step.target));

    if (!step.target) return undefined;

    const tick = () => {
      if (cancelled) return;
      const r = getRect(step.target);
      if (r) {
        if (!scrolled) {
          scrolled = true;
          const vh = window.innerHeight;
          const offscreen = r.top < 70 || r.top + r.height > vh - 40;
          if (offscreen) {
            try {
              r.el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            } catch {
              r.el.scrollIntoView();
            }
          }
        }
        setRect(r);
        setSearching(false);
      } else if (Date.now() - started > 4000) {
        setSearching(false); // centered fallback
        setRect(null);
      }
    };

    tick();
    const iv = setInterval(tick, 120);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [stepIndex, step.target, location.pathname]);

  // Measure the card so placement math uses its real height.
  useEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  });

  // Keyboard: Esc exits, →/Enter advances, ← goes back.
  const keyHandler = useCallback(
    (e) => {
      if (e.key === 'Escape') onSkip();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        // Don't hijack Enter inside inputs/dialogs the user may be using.
        const tag = document.activeElement?.tagName;
        if (e.key === 'Enter' && (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')) return;
        onNext();
      } else if (e.key === 'ArrowLeft') onBack();
    },
    [onSkip, onNext, onBack]
  );
  useEffect(() => {
    window.addEventListener('keydown', keyHandler);
    return () => window.removeEventListener('keydown', keyHandler);
  }, [keyHandler]);

  if (searching) return null; // avoids backdrop flash during page loads

  const spotlight = step.target && rect;
  const isLast = stepIndex === steps.length - 1;

  const pos = spotlight
    ? cardPosition(rect, step.placement, cardH)
    : {
        top: window.innerHeight / 2 - cardH / 2,
        left: window.innerWidth / 2 - Math.min(CARD_W, window.innerWidth - 24) / 2,
        width: Math.min(CARD_W, window.innerWidth - 24),
        side: null,
      };

  return (
    <>
      {tourKeyframes}

      {/* Backdrop: cutout glides between targets; plain dim for centered steps.
          pointer-events: none keeps the highlighted element fully usable. */}
      {spotlight ? (
        <Box
          sx={{
            position: 'fixed',
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 2,
            outline: '2px solid rgba(255,255,255,0.95)',
            pointerEvents: 'none',
            zIndex: Z,
            transition: 'top .35s cubic-bezier(.4,0,.2,1), left .35s cubic-bezier(.4,0,.2,1), width .35s cubic-bezier(.4,0,.2,1), height .35s cubic-bezier(.4,0,.2,1)',
            animation: 'repsitTourPulse 2.2s ease-out infinite',
          }}
        />
      ) : (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(8, 15, 28, 0.42)',
            pointerEvents: 'none',
            zIndex: Z,
            animation: 'repsitTourFadeIn .25s ease-out',
          }}
        />
      )}

      {/* Popover card — remounts per step to replay the entrance animation */}
      <Paper
        key={stepIndex}
        ref={cardRef}
        elevation={12}
        sx={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: pos.width,
          zIndex: Z + 1,
          p: 0,
          borderRadius: 3,
          overflow: 'visible',
          animation: 'repsitTourCardIn .28s cubic-bezier(.4,0,.2,1)',
          transition: 'top .35s cubic-bezier(.4,0,.2,1), left .35s cubic-bezier(.4,0,.2,1)',
        }}
      >
        {spotlight && pos.side && <Box sx={arrowStyle(rect, pos)} />}

        <Box sx={{ p: 2.25, pb: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={`${stepIndex + 1} of ${steps.length}`}
              size="small"
              color="primary"
              sx={{ fontWeight: 700, height: 22 }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" onClick={onSkip} aria-label="Exit tour" sx={{ mr: -0.75, mt: -0.25 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.3 }}>
            {step.title}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
            {step.body}
          </Typography>
          {/* Target expected but not on the page — tell the user why instead
              of showing a centered card that implies everything is fine. */}
          {step.target && !rect && step.missingHint && (
            <Typography
              variant="body2"
              sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'warning.light', color: 'warning.contrastText', lineHeight: 1.5 }}
            >
              {step.missingHint}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 0.75 }}>
            {/* progress dots */}
            <Box sx={{ display: 'flex', gap: 0.6, flexGrow: 1 }}>
              {steps.map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: i === stepIndex ? 18 : 7,
                    height: 7,
                    borderRadius: 4,
                    bgcolor: i === stepIndex ? 'primary.main' : i < stepIndex ? 'primary.light' : 'action.disabled',
                    transition: 'all .25s',
                  }}
                />
              ))}
            </Box>
            {stepIndex > 0 && (
              <Button size="small" color="inherit" onClick={onBack} sx={{ textTransform: 'none' }}>
                Back
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={onNext}
              sx={{ textTransform: 'none', px: 2, borderRadius: 2, fontWeight: 700 }}
            >
              {isLast ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
}
